import {useState} from 'react'
import {Link, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {ObraFormDialog} from '@/components/obra-form-dialog'
import {PlanLockedPage} from '@/components/plan-upsell'
import {Button} from '@/components/ui/button'
import {ObraGente} from '@/components/obra-gente'
import {ObraPeligros} from '@/components/obra-peligros'
import {QuienEntraHoy} from '@/components/quien-entra-hoy'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useObra} from '@/hooks/empresa/obras/use-obras'

/** Gente es captura ordinaria, peligros es acto de firmante y «quién entra hoy» es solo lectura (E10-H16).
 * El brazo de error existe porque react-query se rinde en silencio tras dos reintentos. */
export function ObraDetallePage() {
  const {t} = useTranslation()
  const {businessId, proyectoId} = useParams<{businessId: string; proyectoId: string}>()
  const [params, setParams] = useSearchParams()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const obra = useObra(business ? businessId : undefined, proyectoId)
  const [editando, setEditando] = useState(false)

  if (!businessId || !proyectoId) return null
  if (guard) return guard
  if (capabilities.plan_tier === 'free') {
    return <PlanLockedPage feature={t('plans.feature.obras')} businessId={businessId} businessName={business!.name} />
  }
  if (obra.isError) {
    return (
      <div className="space-y-3">
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="obra-detalle-error-carga">
          {t('obra.errorCargaObra')}
        </p>
        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" onClick={() => void obra.refetch()}>
            {t('obra.reintentar')}
          </Button>
          <Link
            to={`/businesses/${businessId}/obras`}
            className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase hover:underline">
            {t('obra.detail.back')}
          </Link>
        </div>
      </div>
    )
  }
  if (!obra.data) return <Skeleton className="h-64 w-full" />

  const activa = obra.data.estado === 'activo'
  const canEdit = !godReadOnly && activa

  return (
    <div className="space-y-5" data-testid="obra-detalle">
      <div className="space-y-2">
        <Link
          to={`/businesses/${businessId}/obras`}
          className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase hover:underline">
          {t('obra.detail.back')}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <PageHeading
            title={obra.data.name}
            subtitle={
              obra.data.fecha_fin
                ? t('obra.detail.periodoCerrado', {inicio: obra.data.fecha_inicio, fin: obra.data.fecha_fin})
                : t('obra.detail.periodo', {inicio: obra.data.fecha_inicio})
            }
          />
          <div className="flex items-center gap-2">
            <Tag tone="neutral">{t(`obra.estado.${obra.data.estado}`)}</Tag>
            {!godReadOnly && (
              <Button size="sm" variant="outline" onClick={() => setEditando(true)} data-testid="obra-editar">
                {t('obra.detail.edit')}
              </Button>
            )}
          </div>
        </div>
        {!activa && (
          <p
            className="rounded-lg bg-secondary/60 px-4 py-3 text-sm text-foreground"
            data-testid="obra-terminada-aviso">
            {t('obra.detail.terminadaAviso')}
          </p>
        )}
      </div>

      {/* La pestaña vive en la URL: volver de una decisión conserva la pestaña y sus filtros. */}
      <Tabs
        value={params.get('tab') ?? 'gente'}
        onValueChange={(valor) =>
          setParams(
            (previos) => {
              previos.set('tab', valor)
              return previos
            },
            {replace: true}
          )
        }>
        <TabsList>
          <TabsTrigger value="gente" data-testid="obra-tab-gente">
            {t('obra.tabs.gente')}
          </TabsTrigger>
          <TabsTrigger value="peligros" data-testid="obra-tab-peligros">
            {t('obra.tabs.peligros')}
          </TabsTrigger>
          <TabsTrigger value="ingreso" data-testid="obra-tab-ingreso">
            {t('obra.tabs.quienEntraHoy')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="gente" className="pt-4">
          <ObraGente businessId={businessId} proyectoId={proyectoId} obra={obra.data} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="peligros" className="pt-4">
          {/* Confirmar es `require_firmante`, no solo «fuera de modo dios»; una obra terminada no acepta cambios. */}
          <ObraPeligros
            businessId={businessId}
            proyectoId={proyectoId}
            canEdit={canEdit}
            puedeFirmar={capabilities.sgsst_firmar}
            razonBloqueo={
              godReadOnly
                ? t(capabilities.sgsst_firmar ? 'obra.bloqueoSeguro' : 'obra.bloqueoModoDios')
                : capabilities.firmar_bloqueado_por
            }
          />
        </TabsContent>
        <TabsContent value="ingreso" className="pt-4">
          <QuienEntraHoy businessId={businessId} proyectoId={proyectoId} />
        </TabsContent>
      </Tabs>

      <ObraFormDialog businessId={businessId} obra={obra.data} open={editando} onOpenChange={setEditando} />
    </div>
  )
}
